import { Injectable, Signal, computed, signal } from '@angular/core';
import type { IAuthAccessToken, IAuthLoginPayload } from '@account/data-access';
import { Encrypt } from '@shared/utils';
import { Subject, Observable, finalize, take } from 'rxjs';

export interface TemporaryAuthModalConfig {
  requiredPermissions: number[];
  /**
   * Optional modal title. Defaults to a generic warning.
   */
  title?: string;
  /**
   * Optional modal description shown above the form.
   */
  message?: string;
  /**
   * Amount of time in milliseconds that the temporary access should remain active.
   * Defaults to 30 seconds.
   */
  durationMs?: number;
  /**
   * Function responsible for performing authentication with the provided payload.
   * Must return an observable that emits the access token on success.
   * If not provided, the modal cannot authenticate and will show an error.
   */
  authProvider?: (payload: IAuthLoginPayload) => Observable<IAuthAccessToken>;
}

export interface TemporaryAuthModalResult {
  status: 'success' | 'cancel';
  authData?: IAuthAccessToken;
  durationMs?: number;
}

export interface TemporaryAuthModalState {
  visible: boolean;
  title: string;
  message: string;
  requiredPermissions: number[];
  durationMs: number;
  loading: boolean;
  /**
   * True while we have a successful auth result pending the closing animation.
   * Used by the component to resume the Lottie animation immediately on success.
   */
  authSuccess: boolean;
  errorMessage: string | null;
}

const DEFAULT_DURATION = 30_000;
const TRANSLATION_PATH = 'TEMPORARY_AUTH_MODAL';

const INITIAL_STATE: TemporaryAuthModalState = {
  visible: false,
  title: '',
  message: '',
  requiredPermissions: [],
  durationMs: DEFAULT_DURATION,
  loading: false,
  authSuccess: false,
  errorMessage: null,
};

@Injectable({ providedIn: 'root' })
export class TemporaryAuthModalService {
  private readonly _state = signal<TemporaryAuthModalState>(INITIAL_STATE);
  private _result$ = new Subject<TemporaryAuthModalResult>();
  private _authProvider: ((payload: IAuthLoginPayload) => Observable<IAuthAccessToken>) | null = null;
  private _closeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly encrypt: Encrypt,
  ) {
    this.resetState();
  }

  /**
   * Modal state used by the component template.
   */
  readonly state: Signal<TemporaryAuthModalState> = computed(() => this._state());

  /**
   * Opens the modal with the provided configuration and returns an observable
   * that emits when the user authenticates or cancels the dialog.
   */
  open(config: TemporaryAuthModalConfig): Observable<TemporaryAuthModalResult> {
    this.completeResult({ status: 'cancel' });

    const { title, message, durationMs, requiredPermissions } = config;
    this._authProvider = config.authProvider ?? null;
    this._state.set({
      ...INITIAL_STATE,
      visible: true,
      title: title ?? this.getDefaultTitle(),
      message: message ?? this.getDefaultMessage(),
      durationMs: durationMs ?? DEFAULT_DURATION,
      requiredPermissions,
    });

    this._result$ = new Subject<TemporaryAuthModalResult>();
    return this._result$.asObservable();
  }

  /**
   * Closes the modal and notifies listeners that the flow has been cancelled.
   */
  cancel(): void {
    if (!this._state().visible) {
      return;
    }

    if (this._closeTimer) {
      clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }
    this.resetState();
    this.completeResult({ status: 'cancel' });
  }

  /**
   * Attempts to authenticate with the provided credentials.
   */
  authenticate(email: string, password: string): void {
    const trimmedEmail = email?.trim();
    if (!trimmedEmail || !password) {
      this._state.update((state) => ({
        ...state,
        errorMessage: `${TRANSLATION_PATH}.ERRORS.INVALID_CREDENTIALS`,
      }));
      return;
    }

    if (!this._authProvider) {
      this._state.update((state) => ({
        ...state,
        errorMessage: `${TRANSLATION_PATH}.ERRORS.MISSING_AUTH_PROVIDER`,
      }));
      return;
    }

    const currentState = this._state();
    const payload: IAuthLoginPayload = {
      Email: trimmedEmail,
      Password: this.encrypt.encryptMd5(trimmedEmail.toLowerCase() + password),
      Origin: 'actuar',
    };

    this._state.update((state) => ({ ...state, loading: true, errorMessage: null, authSuccess: false }));

    this._authProvider(payload)
      .pipe(take(1))
      .subscribe({
        next: (authData) => {
          // Flag success so the component can resume the animation immediately
          this._state.update((state) => ({ ...state, authSuccess: true }));

          // Keep loading true during the success animation, then close + emit result
          this._closeTimer = setTimeout(() => {
            this._state.update((state) => ({ ...state, loading: false }));
            this.resetState();
            this.completeResult({
              status: 'success',
              authData,
              durationMs: currentState.durationMs ?? DEFAULT_DURATION,
            });
            this._closeTimer = null;
          }, 3000);
        },
        error: (error) => {
          const validationMessage =
            error?.error?.ValidationResult?.Errors?.[0]?.ErrorMessage ??
            error?.error?.Message ??
            `${TRANSLATION_PATH}.ERRORS.GENERIC`;
          this._state.update((state) => ({
            ...state,
            loading: false,
            authSuccess: false,
            errorMessage: validationMessage,
          }));
        },
      });
  }

  private completeResult(result: TemporaryAuthModalResult): void {
    if (!this._result$ || this._result$.closed) {
      return;
    }

    this._result$.next(result);
    this._result$.complete();
  }

  private resetState(): void {
    this._state.set({
      ...INITIAL_STATE,
      title: this.getDefaultTitle(),
      message: this.getDefaultMessage(),
    });
    this._authProvider = null;
  }

  private getDefaultTitle(): string {
    return `${TRANSLATION_PATH}.MODAL_TITLE`;
  }

  private getDefaultMessage(): string {
    return `${TRANSLATION_PATH}.MODAL_MESSAGE`;
  }
}
